<template>
  <b-container>
    <LoadingIndicator 
      v-if="loading"
    />
    <div v-if="!loading">
      <b-row>
        <b-col>
          <BaseMessageBlock :type="'Warning'" :messages="warnings"></BaseMessageBlock>
        </b-col>
      </b-row>
      <ProjectDetails
        v-if="registration"
        :projectName="`Welcome to ${projectName}`"
        :residentEngineer="ownerName"
        :projectManager="contractorName"
        :facilitatorName="facilitatorName"
        :organizationLogo="ownerLogo"
        :contractorLogo="contractorLogo"
      />
      <b-row>
        <b-col></b-col>
        <b-col cols="auto" style="width: 400px;" class="widget p-4 mt-5">
          <h2>Sign up</h2>

          <p
            class="text-muted d-flex flex-column align-items-end position-absolute"
            style="font-size: 12px; top: 24px; right: 24px"
          >
            Already have an account?
            <router-link to="/login">Log in</router-link>
          </p>
          <b-row>
            <b-col>
              <b-row class="mt-2">
                <b-col>
                  <button class="ms-button" @click="loginMS()">
                    <img style="float: left;" src="/assets/microsoft-icon.png" />Sign up with Microsoft
                  </button>
                </b-col>
              </b-row>
              <b-row class="text-center mt-3">
                <b-col>
                  <strong>Or</strong>
                  <hr />
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form @submit.prevent="register">
                    <b-form-group v-if="registration" label="Name" label-for="name">
                      <b-form-input id="name" v-model="name" required></b-form-input>
                    </b-form-group>
                    <b-form-group label="Email" label-for="email">
                      <b-form-input id="email" v-model="email" :disabled="!registration"></b-form-input>
                    </b-form-group>
                    <b-form-group
                      label="Password"
                      label-for="create-password"
                      :invalid-feedback="newPasswordFeedback"
                      :state="errors.length == 0 ? null : newPasswordState"
                    >
                      <b-form-input
                        id="create-password"
                        type="password"
                        :state="errors.length == 0 ? null : newPasswordState"
                        v-model="form.password"
                        required
                      ></b-form-input>
                    </b-form-group>
                    <b-form-group
                      label="Confirm Password"
                      label-for="confirm-password"
                      :invalid-feedback="confirmPasswordFeedback"
                      :state="errors.length == 0 ? null : confirmPasswordState"
                      description="Please re-enter password."
                    >
                      <b-form-input
                        id="confirm-password"
                        type="password"
                        :state="errors.length == 0 ? null : confirmPasswordState"
                        v-model="form.confirmPassword"
                        required
                      ></b-form-input>
                    </b-form-group>
                    <b-button
                      type="submit"
                      variant="primary"
                      class="float-right ml-2"
                      :disabled="sending"
                      style="width: 100%"
                    >
                      <div v-if="!sending">Register</div>
                      <b-spinner small v-if="sending" label="Spinning"></b-spinner>
                    </b-button>
                  </b-form>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <BaseMessageBlock :messages="errors"></BaseMessageBlock>
              <BaseMessageBlock :type="'Success'" :messages="messages"></BaseMessageBlock>
            </b-col>
          </b-row>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";
import authMixin from "@/mixins/auth-mixin.js";
import { v4 as uuidv4 } from "uuid";

import ProjectDetails from "@/components/project/ProjectDetails.vue";

export default {
  name: "SignUp",
  mixins: [authMixin],
  components: {
    ProjectDetails,
  },
  data() {
    return {
      errors: [],
      messages: [],
      warnings: [],
      sending: false,
      loading: false,
      form: {
        password: "",
        confirmPassword: "",
      },
      registration: false,

      projectId: null,
      projectName: "",
      ownerName: "",
      ownerLogo: "",
      contractorName: "",
      contractorLogo: "",
      facilitatorName: "",

      name: "",
      email: "",
      userId: "",
      surveyId: "",
    };
  },
  mounted() {
    this.loading = true;

    let id = this.$route.query.uid;

    this.projectId = this.$route.query.pid;
    this.surveyId = this.$route.query.sid;

    if (!id && !this.projectId) {
      this.errors.push({ message: "Missing Required ID" });
      this.loading = false;
    } else if (id) {
      axios
        .get(
          `${this.$api_url}/users/${id}/account-setup-data`,
          this.getReqConfig()
        )
        .then((response) => {
          if (response.data.hasPassword) {
            let query = {
              uid: id,
            };
            if (this.surveyId) {
              query.sid = this.surveyId;
            }
            this.$router.push({ name: "Account Setup", query: query });
            return;
          }
          if (this.surveyId) {
            this.warnings.push({ message: "Please sign up to see the survey results."})
          }
          this.userId = id;
          this.email = response.data.email;
          this.loading = false;
        })
        .catch((err) => {
          if (this.authenticated) {
            this.defaultRedirect();
          } else {
            this.$router.push({
              name: "Login",
              query: { error: err.response.data[0] },
            });
          }
        });
    } else {
      // Get project details first
      let url = `${this.$api_url}/projects/${this.projectId}/info`;
      axios
        .get(url, this.getReqConfig())
        .then((res) => {
          this.projectName = res.data.projectName;
          this.ownerName = res.data.primaryOwnerName;
          this.ownerLogo = res.data.ownerLogo;
          this.contractorName = res.data.primaryContractorName;
          this.contractorLogo = res.data.contractorLogo;
          this.facilitatorName = res.data.facilitatorName;

          this.userId = this.generateUUID();
          this.registration = true;

          localStorage.setItem("tempProjectId", this.projectId);
          localStorage.setItem("tempUserId", this.userId);
        })
        .catch(() => {
          this.errors.push({
            message: "Couldn't find project with specified ID",
          });
          this.loading = false;
        })
        .finally(() => (this.loading = false));
    }
  },
  methods: {
    register() {
      if (!this.validateForm()) {
        return;
      }

      this.errors = [];
      this.sending = true;

      localStorage.setItem("tempEmail", this.email);
      localStorage.setItem("tempPass", this.form.password);

      if (this.registration) {
        let request = {
          userId: this.userId,
          password: this.form.password,
          projectId: this.projectId,
          hasMicrosoft: false,
          name: this.name,
          email: this.email,
        };
        this.registerNewUser(request);
      } else {
        let request = {
          userId: this.userId,
          password: this.form.password,
        };
        this.registerExistingUser(request);
      }
    },
    registerNewUser(request) {
      let url = `${this.$api_url}/users/register-account`;

      axios
        .post(url, request, this.getReqConfig())
        .then(() => {
          let query = {
            uid: this.userId,
          };
          this.$router.push({ name: "Account Setup", query: query });
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.sending = false;
        });
    },
    registerExistingUser(request) {
      let url = `${this.$api_url}/users/register-account`;

      let query = {
        uid: this.userId,
      };

      axios
        .post(url, request, this.getReqConfig())
        .then(() => {
          if (this.surveyId){
            query.sid = this.surveyId;
          }
          this.$router.push({ name: "Account Setup", query: query });
        })
        .catch((err) => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.sending = false;
        });
    },
    validateForm() {
      let errorCount = this.errors.length;

      if (!this.newPasswordState) {
        this.errors.push({
          message:
            "Password must be at least 6 characters long, have at least 1 lowercase letter, at least 1 uppercase letter, at least 1 number, and at least 1 non-alphanumeric character",
        });
      }
      if (!this.confirmPasswordState) {
        this.errors.push({ message: "Please re-enter password." });
      }

      return errorCount == this.errors.length;
    },
    loginMS() {
      if (this.surveyId) {
        this.$store.dispatch('setCurrentSurvey', this.surveyId);
      }
      this.$authService.msal.setActiveAccount(null);
      this.$authService.signIn().then((response) => {
        if (response) {
          // This probably doesn't work anymore after the redirect for MS sign in change
          this.$authService.msal.setActiveAccount(response.account);
          this.$auth
            .setupAuth(response)
            .then(() => {
              this.defaultRedirect();
            })
            .catch((err) => {});
        }
      });
    },
    generateUUID() {
      return uuidv4();
    },
  },
  computed: {
    newPasswordState() {
      return this.$validate.passwordState(this.form.password);
    },
    newPasswordFeedback() {
      return this.$validate.passwordFeedback(this.form.password);
    },
    confirmPasswordState() {
      return this.$validate.confirmPasswordState(
        this.form.password,
        this.form.confirmPassword
      );
    },
    confirmPasswordFeedback() {
      return this.$validate.confirmPasswordFeedback(
        this.form.password,
        this.form.confirmPassword
      );
    },
  },
  watch: {
    accountUser(val) {
      if (val != null && val.setup === false) {
        this.$store.dispatch("setPauseAuth", true);
      }
    },
  },
};
</script>

<style>
.ms-button {
  font-family: "Segoe UI";
  font-size: 15px;
  font-weight: 600;
  color: #5e5e5e;
  background-color: #ffffff;
  border: 1.5px solid #8c8c8c;
  height: 41px;
  width: 100%;
  padding: 0 12px;
}
</style>